/*
 * DATA_RIGHTS: HONEYWELL CONFIDENTIAL AND PROPRIETARY
 * THIS WORK CONTAINS VALUABLE CONFIDENTIAL AND PROPRIETARY
 * INFORMATION. DISCLOSURE, USE OR REPRODUCTION OUTSIDE OF
 * HONEYWELL INTERNATIONAL, INC. IS PROHIBITED EXCEPT AS
 * AUTHORIZED IN WRITING. THIS UNPUBLISHED WORK IS PROTECTED BY
 * THE LAWS OF THE UNITED STATES AND OTHER COUNTRIES. IN THE
 * EVENT OF PUBLICATION, THE FOLLOWING NOTICE SHALL APPLY:
 * Copyright (C) 2022 HONEYWELL INTERNATIONAL, INC., ALL RIGHTS RESERVED.
 *
 * Created by H144765 on 4/4/2022.
 */

import React from 'react'
import {withRouter} from 'react-router'
import {connect} from 'react-redux'
import {bindActionCreators} from 'redux'
import {FormattedMessage, injectIntl} from 'react-intl';
import * as FaultActions from '../FaultActions'
import * as SecurityActions from '../../security/SecurityActions'
import StepWizard from '../../common/StepWizard'
import Observations from './Observations'
import Actions from './Actions'
import Summary from './Summary'
import Verification from './Verification'
import {Button, Grid, Icon, Modal, Notification} from '@scuf/common';
import {ACCESS, FAULT_STATES, isClosedState} from '../common/FaultsConst'
import {verifyAirtimeException} from "../../utils/Helpers";
import Loader from '../../common/Loader'
import ErrorTitle from "../../common/ErrorTitle";
import {userHasActivity} from "../../security/SecurityUtils";
import {ACTIVITY_TYPES} from "../../security/SecurityConst";
import UsersSelect from "../common/UsersSelect";
import CloseFaultModal from "../common/CloseFaultModal";
import PRInfoModal from "../common/PRInfoModal";
import ExpandableCard from "../../common/ExpandableCard";
import SimilarFaultCards from "../similarFaults/SimilarFaultCards";
import ActionLogModal from "../common/ActionLogModal";
import CommentsModal from "../common/CommentsModal";
import {showNotification} from "../../common/ToastNotification";

class Troubleshooting extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            activeStep: -1, //only set active step after fetching FaultDetails to avoid Observations component from mounting twice
            assigneeModalOpen: false,
            closeModalOpen: false,
            PRInfoOpen: false,
            similarFaultsModal: false,
            activityLogModal: false,
            commentsModal: false,
        }

        const {intl} = this.props

        this.steps = userHasActivity(this.props.userProfile, ACTIVITY_TYPES.HDA_FUNCTIONALITIES) ?
            [
                intl.formatMessage({id: "fault.troubleshoot.wizard.observations"}),
                intl.formatMessage({id: "fault.troubleshoot.wizard.troubleshooting"}),
                intl.formatMessage({id: "fault.troubleshoot.wizard.summary"}),
            ]
            :
            [
                intl.formatMessage({id: "fault.troubleshoot.wizard.observations"}),
                intl.formatMessage({id: "fault.troubleshoot.wizard.troubleshooting"}),
                intl.formatMessage({id: "fault.troubleshoot.wizard.verification"}),
                intl.formatMessage({id: "fault.troubleshoot.wizard.summary"}),
            ]

        this.changeStep = this.changeStep.bind(this)
        this.navigateToCorrectStep = this.navigateToCorrectStep.bind(this)
        this.submitSetAssignee = this.submitSetAssignee.bind(this)
    }

    componentDidMount() {
        this.props.FaultActions.clearState();

        const { match: { params } } = this.props;
        this.props.FaultActions.setAssetFault(params.assetId, params.faultId, null, null)

        this.reloadFaultCondition(params.assetId, params.faultId)
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.fault.details === null && this.props.fault.details && !this.isUserAssigned()) {
            this.setState({assignee: this.props.security.userProfile.id}, () => this.submitSetAssignee())
        }
    }

    reloadFaultCondition(assetId, faultId) {
        const {history, userProfile} = this.props
        const external = userHasActivity(userProfile, ACTIVITY_TYPES.EXTERNAL_ACTIVITY)
        this.props.FaultActions.fetchDetails(assetId, faultId, external).then(() => {
            if (this.props.fault.error === null) {
                if (this.props.fault.details.faultConditionState === FAULT_STATES.DELETED || isClosedState(this.props.fault.details.faultConditionState)) {
                    external ?
                        history.push(`/airtime/asset/${assetId}/faults/${faultId}`)
                        :
                        history.push(`/assets/${assetId}/faults/${faultId}`)
                } else {
                    this.props.FaultActions.setReadOnly(
                        isClosedState(this.props.fault.details.faultConditionState) ||
                        this.props.fault.details.faultConditionState === FAULT_STATES.ON_HOLD ||
                        !this.isUserAssigned() || this.props.fault.details.access !== ACCESS.WRITE)
                    if (this.props.fault.details.faultConditionState === FAULT_STATES.VERIFICATION) {
                        this.setState({activeStep: 2})
                    } else {
                        this.setState({activeStep: 0})
                    }
                }
            }
        }).catch(ex => {
            verifyAirtimeException(ex.errorKey, this.props.SecurityActions.signOut)
        })
    }

    changeStep(step) {
        const {fault, userProfile} = this.props
        if (fault.readOnly) {
            this.setState({activeStep: step})
            return
        }
        //From symptoms to troubleshooting
        if (this.state.activeStep === 0 && step === 1) {
            const external = userHasActivity(userProfile, ACTIVITY_TYPES.EXTERNAL_ACTIVITY)
            const shouldMoveToInProgress = fault.details.faultConditionState === FAULT_STATES.NEW
            this.props.FaultActions.submitObservations(fault.assetId, fault.faultId, fault.observations, external).then(() => {
                    if (shouldMoveToInProgress) {
                        return this.props.FaultActions.setFaultState(fault.assetId, fault.faultId, FAULT_STATES.IN_PROGRESS, [], null, external)
                    }
                }
            ).then(() => {
                    this.navigateToCorrectStep()
                }
            ).catch(ex => {
                verifyAirtimeException(ex.errorKey, this.props.SecurityActions.signOut)
            })
            //From troubleshooting to verification
        } else if (this.state.activeStep === 1 && step === 2) {
            this.navigateToCorrectStep()
            //From verification to summary
        } else if (this.state.activeStep === 2 && step === 3) {
            this.props.FaultActions.setFaultState(fault.assetId, fault.faultId, FAULT_STATES.CLOSED_RESOLVED, fault.verificationObservations, null, userHasActivity(userProfile, ACTIVITY_TYPES.EXTERNAL_ACTIVITY)).then(() => {
                    this.navigateToCorrectStep()
                }
            ).catch(ex => {
                verifyAirtimeException(ex.errorKey, this.props.SecurityActions.signOut)
            })
        } else {
            this.setState({activeStep: step})
        }
    }

    /**
     * Method decides based on faultConditionState to which step the user should be navigated. Updated fault details are always returned
     * as result for setObservations, setTestResult and setCorrectiveAction calls
     */
    navigateToCorrectStep() {
        const {fault} = this.props
        if (fault.details.faultConditionState === FAULT_STATES.IN_PROGRESS) {
            this.setState({activeStep: 1})
        } else if (fault.details.faultConditionState === FAULT_STATES.VERIFICATION) {
            this.setState({activeStep: 2})
        } else if (isClosedState(fault.details.faultConditionState)) {
            this.setState({activeStep: 3})
        } else {
            // Do not change the current step
        }
    }

    isUserAssigned() {
        if (this.props.fault.details.assignedUser) {
            return this.props.fault.details.assignedUser.id === this.props.security.userProfile.id
        }
        return false
    }

    assigneeModal() {
        const {intl, fault} = this.props
        return (
            <Modal closeIcon={false} onClose={() => this.setState({assigneeModalOpen: false})} open={this.state.assigneeModalOpen} closeOnDimmerClick={false} className="assign-modal">
                <Modal.Header>
                    <FormattedMessage id="fault.detail.action.assignee.title"/>
                </Modal.Header>
                <Modal.Content scrolling={false}>
                    <UsersSelect selectedUser={this.state.assignee}
                                 childFleetId={fault.details.asset.fleetId}
                                 label={intl.formatMessage({id: "fault.detail.label.user"})}
                                 onChange={(userId) => this.setState({assignee: userId})}
                                 fluid/>
                </Modal.Content>
                <Modal.Footer>
                    <div className="assignModal-buttons">
                        <Button className="mobile-max-width-button" content={intl.formatMessage({id: "fault.detail.action.cancel"})} onClick={() => this.setState({assigneeModalOpen: false})} size="medium" type="secondary" />
                        <Button className="mobile-max-width-button" content={intl.formatMessage({id: "fault.detail.action.assign.submit"})} onClick={this.submitSetAssignee} size="medium" type="primary" />
                    </div>
                </Modal.Footer>
                <Loader loading={this.props.loading}/>
            </Modal>
        )
    }

    submitSetAssignee() {
        this.props.FaultActions.assignUser(this.props.fault.details.asset.id, this.props.fault.details.id, this.state.assignee,
            userHasActivity(this.props.userProfile, ACTIVITY_TYPES.EXTERNAL_ACTIVITY)).then(() => {
            this.reloadFaultCondition(this.props.fault.details.asset.id, this.props.fault.details.id)
            this.setState({assigneeModalOpen: false})
        }).catch((err) => {
            this.setState({assigneeModalOpen: false})
            if (err.generalError === false) {
                showNotification({
                    translationPropertyKey: 'fault.detail.action.assign.' + err.errorKey,
                    severity: "critical",
                });
            }
        })
    }

    closeFaultModal() {
        return (
            <CloseFaultModal
                closeModalWindow={() => this.setState({closeModalOpen: false})}
                closeModalOpen={this.state.closeModalOpen}
                reloadFaultCondition={() => this.props.reloadFaultCondition()}
                troubleshootingNavigation={this.navigateToCorrectStep}
            />
        )
    }

    renderPRInfoModal() {
        return (
            <PRInfoModal
                closeModalWindow={() => this.setState({PRInfoOpen: false})}
                modalWindowOpen={this.state.PRInfoOpen}
            />
        )
    }

    renderActivityLogModal() {
        return (
            <ActionLogModal
                closeModalWindow={() => this.setState({activityLogModal: false})}
                modalWindowOpen={this.state.activityLogModal}
            />
        )
    }

    renderCommentModal() {
        return (
            <CommentsModal
                closeModalWindow={() => this.setState({commentsModal: false})}
                modalWindowOpen={this.state.commentsModal}
            />
        )
    }

    renderSimilarFaultsModal() {
        const {fault, intl} = this.props
        return (
            <Modal id="similar-faults" closeIcon={false} onClose={() => this.setState({similarFaultsModal: false})} open={this.state.similarFaultsModal}
                   closeOnDimmerClick={true} className="closed-modal">
                <Modal.Header>
                    <FormattedMessage id="fault.similarFaults"/>
                </Modal.Header>
                <Modal.Content scrolling={true} className="modal--content">
                    <Grid>
                        <Grid.Row id="similar-faults">
                            <Grid.Column width={12}>
                                <ExpandableCard className="grid--card" title={
                                    intl.formatMessage({id: "fault.similarFaults.onAsset"},
                                        {count: fault.details.similarFaultsOnAsset == null ? 0 : fault.details.similarFaultsOnAsset.length})}
                                                content={<SimilarFaultCards faults={fault.details.similarFaultsOnAsset} numberOfAllocatedSymptoms={fault.details.numberOfAllocatedSymptoms}/>}/>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row id="similar-faults">
                            <Grid.Column width={12}>
                                <ExpandableCard className="grid--card" title={
                                    intl.formatMessage({id: "fault.similarFaults.onDifferentAssets"},
                                        {count: fault.details.similarFaultsOnDifferentAssets == null ? 0 : fault.details.similarFaultsOnDifferentAssets.length})}
                                                content={<SimilarFaultCards faults={fault.details.similarFaultsOnDifferentAssets} numberOfAllocatedSymptoms={fault.details.numberOfAllocatedSymptoms}/>}/>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Modal.Content>
                <Modal.Footer>
                    <div className="closedModal-buttons">
                        <Button className="mobile-max-width-button"
                                content={intl.formatMessage({id: "fault.detail.action.close"})}
                                onClick={() => this.setState({similarFaultsModal: false})} size="medium" type="secondary"/>
                    </div>
                </Modal.Footer>
                <Loader loading={this.props.loading}/>
            </Modal>
        )
    }

    renderSidePanel() {
        return (
            <div id="troubleshooting-panel">
                <button className="button" onClick={() => this.setState({PRInfoOpen: true})}>
                    <Icon className="item" root="common" name="badge-info" size="large"/>
                    <span className="item">PR Info</span>
                </button>
                <button className="button" onClick={() => this.setState({similarFaultsModal: true})}>
                    <Icon className="item" root="common" name="template" size="large"/>
                    <span className="item">Similar PR</span>
                </button>
                <button className="button" onClick={() => this.setState({activityLogModal: true})}>
                    <Icon className="item" root="common" name="user" size="large"/>
                    <span className="item">Activity Log</span>
                </button>
                <button className="button" onClick={() => this.setState({commentsModal: true})}>
                    <Icon className="item" root="common" name="edit" size="large"/>
                    <span className="item">Comments</span>
                </button>
                <button className={"button" + (this.state.activeStep === 3 ? " disabled" : "")} onClick={() => this.setState({assigneeModalOpen: true})}
                        disabled={this.state.activeStep === 3}>
                    <Icon className="item" root="common" name="user-edit" size="large"/>
                    <span className="item">Assign</span>
                </button>
                <button className={"button" + (this.state.activeStep === 3 ? " disabled" : "")} onClick={() => this.setState({closeModalOpen: true})}
                        disabled={this.state.activeStep === 3}>
                    <Icon className="item" root="common" name="close" size="large"/>
                    <span className="item">Close</span>
                </button>
            </div>
        )
    }

    getActiveComponent() {
        switch (this.state.activeStep) {
            case 0:
                return <Observations nextButtonClick={() => this.changeStep(1)}/>
            case 1:
                return <Actions
                        nextButtonClick={() => this.changeStep(2)}
                        previousButtonClick={() => this.changeStep(0)}
                        reloadFaultCondition={() => this.reloadFaultCondition(fault.assetId, fault.faultId)}
                        troubleshootingNavigation={this.navigateToCorrectStep}
                        showCloseActionModal={() => this.setState({closeModalOpen: true})}
                    />
            case 2:
                return <Verification nextButtonClick={() => this.changeStep(3)}
                                                      previousButtonClick={() => this.changeStep(1)}/>
            case 3:
                return <Summary previousButtonClick={() => this.changeStep(2)} />
        }
    }

    render() {
        const {fault} = this.props

        if (fault.error) {
            return <ErrorTitle title={"fault.detail.error."+fault.error} />
        }

        if (!fault.faultId || !fault.details) {
            return null
        }

        let faultInfoPanel = (
            <div className="fault-info-panel">
                <div className="column">
                    <span className="title">{fault.details.name}: {fault.details.primarySymptom.name}</span>
                    <span className="text">S/N: {fault.details.asset.name}</span>
                </div>
                <div className="column">
                    <span className="text">Assigned to: {fault.details.assignedUser ? fault.details.assignedUser.displayName : ""}</span>
                    {!this.isUserAssigned() &&
                        <div className="buttons">
                            <Button type="link" content="Assign to me" onClick={() => {
                                this.setState({assignee: this.props.security.userProfile.id}, () => this.submitSetAssignee())
                            }}/>
                        </div>
                    }
                </div>
            </div>
        )

        const activeWizardComponent = this.getActiveComponent();

        let readOnlyMessage = [];
        if (fault.readOnly) {
            readOnlyMessage.push(<FormattedMessage key="readonly" id="fault.detail.troubleshooting.readonly"/>)
            if (isClosedState(fault.details.faultConditionState)) {
                readOnlyMessage.push(<FormattedMessage key="closed" id="fault.detail.troubleshooting.readonly.closed"/>)
            } else {
                if (fault.details.access !== ACCESS.WRITE) {
                    readOnlyMessage.push(<FormattedMessage key="nowrite" id="fault.detail.troubleshooting.readonly.nowrite"/>)
                } else if (!this.isUserAssigned()) {
                    readOnlyMessage.push(<FormattedMessage key="user" id="fault.detail.troubleshooting.readonly.user"/>)
                } else {
                    // Leave the readOnly message empty
                }
                if (fault.details.faultConditionState === FAULT_STATES.ON_HOLD) {
                    readOnlyMessage.push(<FormattedMessage key="onhold" id="fault.detail.troubleshooting.readonly.onhold"/>)
                }
            }
            readOnlyMessage =
                <Notification
                    className="read-only-info"
                    hasIcon={true}
                    severity="information">
                    {readOnlyMessage}
                </Notification>
        }

        let mathChangedMessage;
        if (fault.details.mathChanged && !isClosedState(fault.details.faultConditionState)) {
            mathChangedMessage =
                <Notification
                    hasIcon={true}
                    severity="important">
                    <FormattedMessage id="fault.detail.troubleshooting.mathchanged"/>
                </Notification>
        }

        return (
            <div>
                {faultInfoPanel}
                <div id="troubleshooting-main">
                    {this.renderSidePanel()}
                    <div id="troubleshooting">
                        {readOnlyMessage}
                        {mathChangedMessage}
                        <StepWizard steps={this.steps} activeStep={this.state.activeStep} clickable={true}
                                    stepClicked={this.changeStep}/>
                        {activeWizardComponent}
                        <Loader loading={this.props.loading}/>
                        {this.assigneeModal()}
                        {this.closeFaultModal()}
                        {this.state.PRInfoOpen && this.renderPRInfoModal()}
                        {this.renderSimilarFaultsModal()}
                        {this.state.activityLogModal && this.renderActivityLogModal()}
                        {this.state.commentsModal && this.renderCommentModal()}
                    </div>
                </div>
            </div>
        )
    }
}

const mapStateToProps = (state) => {
    return {
        fault: state.fault,
        loading: state.fault.fetchCount > 0,
        security: state.security,
        userProfile: state.security.userProfile,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        FaultActions: bindActionCreators(FaultActions, dispatch),
        SecurityActions: bindActionCreators(SecurityActions, dispatch),
    }
}

export default injectIntl(withRouter(connect(mapStateToProps, mapDispatchToProps)(Troubleshooting)))